import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent, PointerEvent } from "react";

export class BoostHold {
  private readonly pointers = new Set<number>();
  private disabled = false;
  constructor(private readonly change: (pressed: boolean) => void) {}
  press(id: number): void {
    if (this.disabled || this.pointers.has(id)) return;
    const wasHeld = this.pointers.size > 0;
    this.pointers.add(id);
    if (!wasHeld) this.change(true);
  }
  release(id: number): void {
    if (!this.pointers.delete(id)) return;
    if (!this.pointers.size) this.change(false);
  }
  clear(): void {
    if (!this.pointers.size) return;
    this.pointers.clear();
    this.change(false);
  }
  setDisabled(disabled: boolean): void {
    this.disabled = disabled;
    if (disabled) this.clear();
  }
}

export function useBoostHold(
  disabled: boolean,
  onBoost: (pressed: boolean) => void,
) {
  const change = useRef(onBoost);
  change.current = onBoost;
  const [hold] = useState(
    () => new BoostHold((pressed) => change.current(pressed)),
  );
  useEffect(() => hold.setDisabled(disabled), [disabled, hold]);
  useEffect(() => {
    const clear = () => hold.clear();
    const hidden = () => {
      if (document.hidden) clear();
    };
    window.addEventListener("blur", clear);
    window.addEventListener("resize", clear);
    document.addEventListener("visibilitychange", hidden);
    window.visualViewport?.addEventListener("resize", clear);
    window.visualViewport?.addEventListener("scroll", clear);
    return () => {
      window.removeEventListener("blur", clear);
      window.removeEventListener("resize", clear);
      document.removeEventListener("visibilitychange", hidden);
      window.visualViewport?.removeEventListener("resize", clear);
      window.visualViewport?.removeEventListener("scroll", clear);
      clear();
    };
  }, [hold]);
  const release = (event: PointerEvent<HTMLButtonElement>) => {
    hold.release(event.pointerId);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  };
  return {
    onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
      if (disabled || event.button !== 0) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      hold.press(event.pointerId);
    },
    onPointerUp: release,
    onPointerCancel: release,
    onLostPointerCapture: (event: PointerEvent<HTMLButtonElement>) =>
      hold.release(event.pointerId),
    onBlur: () => {
      hold.release(-1);
      hold.release(-2);
    },
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => {
      if (disabled || (event.code !== "Space" && event.code !== "Enter"))
        return;
      event.preventDefault();
      hold.press(event.code === "Space" ? -1 : -2);
    },
    onKeyUp: (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.code === "Space" || event.code === "Enter")
        hold.release(event.code === "Space" ? -1 : -2);
    },
  };
}
